import React, { useContext, useMemo, useState } from "react"
import { Div, Text, Row, Col } from "atomize"
import { motion } from "framer-motion"
import { toast } from "react-toastify"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import TextArea from "../inputs/TextArea"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import { playersContext } from "../providers/players.provider"
import { schoolContext } from "../providers/school.provider"
import { createPlayers } from "../utils/firestore.utils"
import { getPlayerFullName, normalizeName } from "../utils/func.utils"
import { IMPORT_FIELDS, guessFields, parseDelimited, rowsToPlayers } from "../utils/csv.utils"
import StepSwap from "../atoms/StepSwap"
import { useBackSteps } from "../atoms/useBackClose"

// Chi arriva da un altro sistema — o da un foglio tenuto per anni — non
// ribatte quaranta allievi a mano. Si incolla la tabella così com'è: il
// separatore e le colonne li indovina l'app, e quello che sbaglia si
// aggiusta prima di scrivere niente.
export function ImportPlayersModal({ closeModal }) {
	const { activePlayers, archivedPlayers } = useContext(playersContext)
	const { selectedSchool, getSelectedSchool } = useContext(schoolContext)

	const [step, setStep] = useState(0)
	// il tasto indietro del telefono, sul riepilogo, fa quello di Indietro:
	// si torna alla tabella incollata invece di chiudere tutto
	useBackSteps(step, setStep)
	const [text, setText] = useState("")
	const [fields, setFields] = useState([])
	const [isSaving, setIsSaving] = useState(false)

	const table = useMemo(() => (text.trim() === "" ? null : parseDelimited(text)), [text])

	// I nomi già in casa, attivi o archiviati: reimportare lo stesso elenco
	// due volte non deve raddoppiare gli allievi.
	const knownNames = useMemo(() => {
		const names = new Set()
		;[...(activePlayers ?? []), ...(archivedPlayers ?? [])].forEach(player =>
			names.add(normalizeName(getPlayerFullName(player))),
		)
		return names
	}, [activePlayers, archivedPlayers])

	const { toCreate, duplicates } = useMemo(() => {
		if (!table) return { toCreate: [], duplicates: 0 }

		const parsed = rowsToPlayers(table.rows, fields)
		const seen = new Set()
		const toCreate = []
		let duplicates = 0

		parsed.forEach(player => {
			const fullName = normalizeName(getPlayerFullName(player))
			if (knownNames.has(fullName) || seen.has(fullName)) {
				duplicates++
				return
			}
			seen.add(fullName)
			toCreate.push(player)
		})

		return { toCreate, duplicates }
	}, [table, fields, knownNames])

	const readTable = value => {
		setText(value)
		const parsed = value.trim() === "" ? null : parseDelimited(value)
		setFields(parsed ? guessFields(parsed.headers) : [])
	}

	const handleFile = event => {
		const file = event.target.files?.[0]
		if (!file) return

		const reader = new FileReader()
		reader.onload = () => readTable(String(reader.result ?? ""))
		reader.readAsText(file)
	}

	const setField = (index, field) =>
		setFields(prev =>
			prev.map((current, i) => {
				// un campo per colonna: assegnandolo qui si libera da dove stava
				if (i === index) return current === field ? null : field
				return current === field ? null : current
			}),
		)

	const handleImport = async () => {
		setIsSaving(true)
		try {
			const { created } = await createPlayers(toCreate, selectedSchool)
			toast.success(
				`${created} allievi importati${duplicates > 0 ? `, ${duplicates} già c'erano` : ""}`,
				{ position: "top-center" },
			)
			closeModal()
		} catch (err) {
			toast.error(err.message || "Errore durante l'importazione", {
				position: "top-center",
			})
			setIsSaving(false)
		}
	}

	// il primo valore vero della colonna, per capire cosa c'è dentro senza
	// dover leggere l'intestazione di chi l'ha scritta
	const sampleOf = index => table?.rows.find(row => (row[index] ?? "").trim() !== "")?.[index]

	const body = () => {
		if (step === 0) {
			return (
				<>
					<Text textSize="body" textColor={theme.colors.text_light}>
						Apri il tuo foglio, seleziona le righe con le intestazioni e incollale
						qui. Vanno bene anche i file CSV.
					</Text>

					<TextArea
						value={text}
						placeholder={"Nome\tCognome\tSoprannome\nMarco\tRossi\tBibo"}
						handleChange={e => readTable(e.target.value)}
					/>

					<Div textAlign="center">
						<label htmlFor="playersFile" style={{ cursor: "pointer" }}>
							<Text textSize="caption" textColor={theme.colors.primary}>
								oppure scegli un file CSV
							</Text>
						</label>
						<input
							id="playersFile"
							type="file"
							accept=".csv,.txt,text/csv"
							style={{ display: "none" }}
							onChange={handleFile}
						/>
					</Div>

					{table && (
						<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "1rem" }}>
							Riconosciute {table.headers.length} colonne e {table.rows.length} righe.
						</Text>
					)}
				</>
			)
		}

		return (
			<>
				<Text textSize="body" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
					Controlla che ogni colonna sia finita al posto giusto. Si importa solo
					come si chiama l'allievo: le altre colonne restano nel tuo foglio.
				</Text>

				{table?.headers.map((header, index) => (
					<Div key={index} m={{ b: "1rem" }}>
						<Text textSize="body" textWeight="bold">
							{header || `Colonna ${index + 1}`}
						</Text>
						{sampleOf(index) && (
							<Text textSize="caption" textColor={theme.colors.text_light}>
								es. {sampleOf(index)}
							</Text>
						)}

						<Div d="flex" overflow="auto" m={{ t: "0.5rem" }} style={{ gap: "0.5rem" }}>
							{IMPORT_FIELDS.map(({ field, label }) => (
								<motion.div
									key={field}
									whileTap={{ scale: 0.95 }}
									style={{ flexShrink: 0 }}>
									<Div
										p="0.35rem 0.9rem"
										rounded="20px"
										cursor="pointer"
										transition
										bg={
											fields[index] === field
												? theme.colors.gunmetal_fill
												: theme.colors.background
										}
										onClick={() => setField(index, field)}>
										<Text
											textSize="body"
											textColor={
												fields[index] === field
													? theme.colors.white
													: theme.colors.text_light
											}
											style={{ whiteSpace: "nowrap" }}>
											{label}
										</Text>
									</Div>
								</motion.div>
							))}
						</Div>
					</Div>
				))}
			</>
		)
	}

	const canGoOn =
		!isSaving && (step === 0 ? Boolean(table) && table.rows.length > 0 : toCreate.length > 0)

	return (
		<Container>
			<Div m={{ b: "0.25rem" }} textWeight="bold" textSize="title">
				Importa gli allievi
			</Div>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				{step === 0 ? "Da un foglio di calcolo" : `In ${getSelectedSchool()?.schoolName}`}
			</Text>

			<Div maxH="55vh" overflow="auto">
				<Container>
					<StepSwap value={step}>
						{body()}
					</StepSwap>
				</Container>
			</Div>

			{step === 1 && (
				<Paragraph title={`${toCreate.length} da importare`} m={{ t: "0.75rem" }}>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{duplicates > 0
							? `${duplicates} li hai già e vengono saltati.`
							: "Nessun doppione."}{" "}
						{toCreate
							.slice(0, 3)
							.map(player => getPlayerFullName(player))
							.join(", ")}
						{toCreate.length > 3 ? "…" : ""}
					</Text>
				</Paragraph>
			)}

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							textSize="subheader"
							text={step === 0 ? "Annulla" : "Indietro"}
							disabled={isSaving}
							action={step === 0 ? closeModal : () => setStep(0)}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.gunmetal_fill}
							textColor="white"
							textSize="subheader"
							text={
								isSaving
									? "Importo..."
									: step === 0
										? "Avanti"
										: `Importa ${toCreate.length}`
							}
							disabled={!canGoOn}
							action={step === 0 ? () => setStep(1) : handleImport}
						/>
					</Col>
				</Row>
			</Div>
		</Container>
	)
}

export default ImportPlayersModal
